'use client';

import { useEffect, useState } from 'react';
import Button from "../../ui/Button";
import Image from 'next/image';
import Link from 'next/link';
import {
  BenefitsSection,
  Breadcrumbs,
  DispensaryLocations,
  FAQSection,
  Header,
  PageShowcasesProvider,
  ReviewsSection,
  SponsoredBanners,
  SponsoredBannersProvider,
} from '@/components';
import CtaBannerSection from '@/components/sections/CtaBannerSection';
import ProductShowcaseSection from '@/components/sections/ProductShowcaseSection';
import { CalgaryDeliveryServiceSection, WhereWeDeliverSection } from '@/components/sections/sameday-delivery';
import DispensarySeoSection from '@/components/sections/dispensary/DispensarySeoSection';
import ExpandableText from '@/components/ui/ExpandableText';
import type { Store } from '@/lib/types/stores';
import type { DispensaryPageContent } from '@/lib/dispensary-page-content';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

type Props = {
  content: DispensaryPageContent;
};

export default function DispensaryPageTemplate({ content }: Props) {
  const [stores, setStores] = useState<Store[]>([]);
  const heroShowReadMore = content.heroShowReadMore ?? true;
  const heroHideCta = content.heroHideCta ?? false;

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stores`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (data?.stores) setStores(data.stores);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  return (
    <SponsoredBannersProvider>
      <PageShowcasesProvider endpoint={content.productShowcasesEndpoint}>
        <div className="min-h-screen">
          <Header stores={stores} base={API_BASE} />

          <main className="home-page space-y-16 px-4 pb-16 sm:space-y-36 sm:px-8">
            <section className="relative mt-4 overflow-hidden sm:mt-6" aria-labelledby="dispensary-hero-heading">
              <div className="mx-auto w-full">
                <div className="relative w-full overflow-hidden rounded-2xl bg-brand-100 sm:rounded-2xl">
                  <div className="pointer-events-none absolute inset-0 z-0">
                    <Image
                      src="/media/pages/hero-bg-image.webp"
                      alt=""
                      fill
                      priority
                      className="object-cover object-center opacity-[0.05]"
                      sizes="100vw"
                      aria-hidden
                    />
                  </div>

                  <div className="pointer-events-none absolute inset-0 z-[1] bg-brand-100/10" aria-hidden />

                  <div
                    className={`relative z-[2] flex flex-col gap-6 p-4 sm:gap-10 sm:p-6 lg:p-8 ${heroHideCta ? '' : 'lg:flex-row lg:items-end lg:justify-between'
                      }`}
                  >
                    <div className="min-w-0 flex-1">
                      <Breadcrumbs items={content.breadcrumbs} tone="onDark" />

                      <h1
                        id="dispensary-hero-heading"
                        className="text-h2-mobile font-bold leading-tight text-constant-white lg:text-h2-desktop"
                      >
                        {content.heroHeading}
                      </h1>

                      {heroShowReadMore ? (
                        <ExpandableText
                          collapsedHeight={64}
                          className="mt-4 max-w-[52rem]"
                          contentClassName="text-body-16-desktop font-normal leading-relaxed text-white/90"
                          fadeClassName="h-8 bg-gradient-to-t from-blue-100 via-blue-100/85 to-transparent"
                          buttonClassName="mt-3 inline-flex items-center gap-2 self-start rounded-full border border-white/25 bg-white/10 px-1 py-1 pl-4 text-body-sm font-medium text-white transition-colors"
                          expandedButtonClassName="border-white/35 bg-white/15 text-white"
                          iconWrapperClassName="border-white/25 bg-white/10 text-white"
                          expandedIconWrapperClassName="border-white/35 bg-white/15 text-white"
                          chevronColor="white"
                          expandLabel="Continue reading"
                          collapseLabel="Show less"
                        >
                          <p>{content.heroBodyCopy}</p>
                        </ExpandableText>
                      ) : (
                        <p className="mt-4 text-body-16-desktop font-normal leading-relaxed text-constant-white/90">
                          {content.heroBodyCopy}
                        </p>
                      )}
                    </div>

                    {!heroHideCta && (
                      <div className="w-full lg:w-auto lg:shrink-0">
                        <Button asChild variant="glass" color="white" size="lg" className="w-full lg:w-auto rounded-xl">
                          <Link href={content.heroCtaHref}>
                            {content.heroCtaLabel}
                          </Link>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <SponsoredBanners
              position={content.sponsoredPosition}
              limit={1}
              className="w-full"
            />

            <div className='-mx-4 sm:-mx-8'>
              <BenefitsSection />
            </div>

            <DispensaryLocations
              base={API_BASE}
              sectionHeading={content.sectionHeading}
              introLead={content.introLead}
              introRest={content.introRest}
              locationListHeading={content.locationListHeading}
              cityFilter={content.cityFilter}
              defaultMapCenter={content.defaultMapCenter}
            />

            <ProductShowcaseSection slotKey="afterLocations" className="!px-0" />

            <DispensarySeoSection ariaLabel={content.seoAriaLabel} body={content.seoBody} />

            <WhereWeDeliverSection />

            <ReviewsSection />

            {/* The wrapper makes FAQ and the CTA one child of main, so main's
                space-y never lands between them and the FAQ's sponsored banner
                ran straight into the pink panel. The rhythm is repeated here. */}
            <div className="space-y-16 sm:space-y-36 [&>section]:!px-0">
              <FAQSection sponsoredPosition={content.faqSponsoredPosition} />

              <CtaBannerSection
                heading={
                  content.cityFilter === 'Calgary'
                    ? 'Come visit one of our Calgary dispensaries today!'
                    : 'Come visit one of our dispensaries today!'
                }
                description="Browse live menu inventory, place your pickup order online, and stop by your nearest Dank location for a fast, easy cannabis shopping experience."
                /* Was "Take me there" pointing at /dispensary/calgary, which is
                   the page the button sits on. The copy is about ordering for
                   pickup, so it goes to the menu filtered that way. */
                ctaText="Shop for pickup"
                ctaHref="/search?fulfillment=pickup"
                ariaLabel="Visit a Dank dispensary CTA"
              />
            </div>
          </main>
        </div>
      </PageShowcasesProvider>
    </SponsoredBannersProvider>
  );
}
